/** Canvas Global Brand Breath — controlled-random layout generator
 * (VISTOERA_CANVAS_SPLASH_AMBIENT_LANGUAGE_AND_VIEWPORT_RESTORE_V3).
 *
 * V3 ambient field:
 *   - ALL languages mounted at splash start (no staggered appearance)
 *   - random negative animation phase per element (not random appear time)
 *   - depth via initial opacity / layer / size / blur
 *   - single-direction scale groups; unified root fade at 3.7s */

import {
  CANVAS_GLOBAL_BRAND_BREATH_KEYWORDS,
  CANVAS_GLOBAL_SLOGANS,
  type BrandBreathSlogan,
} from './canvasGlobalBrandBreathData'
import {
  BRAND_BREATH_ANCHOR_OPACITY,
  BRAND_BREATH_CYCLE_MS,
  BRAND_BREATH_FAR_OPACITY,
  BRAND_BREATH_KEYWORD_CYCLE_MS,
  BRAND_BREATH_KEYWORD_FONT_PX,
  BRAND_BREATH_KEYWORD_OPACITY,
  BRAND_BREATH_MID_OPACITY,
  BRAND_BREATH_NEAR_OPACITY,
  BRAND_BREATH_TIER1_FONT_PX,
  BRAND_BREATH_TIER2_FONT_PX,
  BRAND_BREATH_TIER3_FONT_PX,
  BRAND_BREATH_TRANSLATE_HARD_MAX_PX,
  BRAND_BREATH_TRANSLATE_MAX_PX,
} from './canvasGlobalBrandBreathVisual'

export interface SafeZone {
  id: string
  xPercentRange: [number, number]
  yPercentRange: [number, number]
}

export const BRAND_BREATH_SAFE_ZONES: SafeZone[] = [
  { id: 'top-left', xPercentRange: [6, 20], yPercentRange: [10, 20] },
  { id: 'top-center-left', xPercentRange: [28, 40], yPercentRange: [7, 16] },
  { id: 'top-center-right', xPercentRange: [60, 72], yPercentRange: [7, 16] },
  { id: 'top-right', xPercentRange: [80, 94], yPercentRange: [10, 20] },
  { id: 'mid-left-upper', xPercentRange: [5, 18], yPercentRange: [30, 40] },
  { id: 'mid-left', xPercentRange: [6, 20], yPercentRange: [45, 55] },
  { id: 'mid-left-lower', xPercentRange: [8, 22], yPercentRange: [62, 72] },
  { id: 'mid-right-upper', xPercentRange: [82, 95], yPercentRange: [30, 40] },
  { id: 'mid-right', xPercentRange: [80, 94], yPercentRange: [45, 55] },
  { id: 'mid-right-lower', xPercentRange: [78, 92], yPercentRange: [62, 72] },
  { id: 'bottom-left', xPercentRange: [6, 20], yPercentRange: [80, 90] },
  { id: 'bottom-center-left', xPercentRange: [30, 42], yPercentRange: [84, 93] },
  { id: 'bottom-center-right', xPercentRange: [58, 70], yPercentRange: [84, 93] },
  { id: 'bottom-right', xPercentRange: [80, 94], yPercentRange: [80, 90] },
]

export type BreathDirection = 'approach' | 'recede' | 'drift' | 'fade-only' | 'anchor-stable'
export type SpatialLayer = 'far' | 'mid' | 'near'

export interface BrandBreathItemLayout {
  locale: string
  text: string
  direction: 'ltr' | 'rtl'
  tier: 1 | 2 | 3
  spatialLayer: SpatialLayer
  isAnchor: boolean
  leftPercent: number
  topPercent: number
  fontSizePx: number
  fontFamily: string
  targetOpacity: number
  translateXPx: number
  translateYPx: number
  breathDirection: BreathDirection
  breathCycleMs: number
  /** Negative ms — random phase into long ambient cycle (§V). */
  phaseDelayMs: number
}

export interface BrandBreathKeywordLayout {
  id: string
  text: string
  leftPercent: number
  topPercent: number
  fontSizePx: number
  targetOpacity: number
  breathCycleMs: number
  phaseDelayMs: number
}

export interface BrandBreathLayout {
  items: BrandBreathItemLayout[]
  keywords: BrandBreathKeywordLayout[]
}

function randomInRange(min: number, max: number): number {
  return min + Math.random() * (max - min)
}

function pick<T>(items: readonly T[]): T {
  return items[Math.floor(Math.random() * items.length)]
}

function shuffled<T>(items: readonly T[]): T[] {
  const copy = [...items]
  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[copy[i], copy[j]] = [copy[j], copy[i]]
  }
  return copy
}

const TIER_FONT_PX: Record<1 | 2 | 3, [number, number]> = {
  1: BRAND_BREATH_TIER1_FONT_PX,
  2: BRAND_BREATH_TIER2_FONT_PX,
  3: BRAND_BREATH_TIER3_FONT_PX,
}

const LAYER_OPACITY: Record<SpatialLayer, [number, number]> = {
  far: BRAND_BREATH_FAR_OPACITY,
  mid: BRAND_BREATH_MID_OPACITY,
  near: BRAND_BREATH_NEAR_OPACITY,
}

function jitterTranslate(reducedMotion: boolean, layer: SpatialLayer): { x: number; y: number } {
  if (reducedMotion) return { x: 0, y: 0 }
  const max = layer === 'far' ? BRAND_BREATH_TRANSLATE_HARD_MAX_PX : BRAND_BREATH_TRANSLATE_MAX_PX
  const scale = layer === 'far' ? 0.85 : layer === 'mid' ? 1 : 0.9
  const effective = max * scale
  const x = randomInRange(-effective, effective)
  const y = randomInRange(-effective, effective)
  return {
    x: Math.max(-BRAND_BREATH_TRANSLATE_HARD_MAX_PX, Math.min(BRAND_BREATH_TRANSLATE_HARD_MAX_PX, x)),
    y: Math.max(-BRAND_BREATH_TRANSLATE_HARD_MAX_PX, Math.min(BRAND_BREATH_TRANSLATE_HARD_MAX_PX, y)),
  }
}

function assignSpatialLayer(index: number, total: number): SpatialLayer {
  const ratio = index / total
  if (ratio < 0.45) return 'far'
  if (ratio < 0.85) return 'mid'
  return 'near'
}

function randomPhaseDelayMs(cycleMs: number): number {
  const phase = randomInRange(0.05, 0.92)
  return -Math.round(cycleMs * phase)
}

function pickMotionForLayer(layer: SpatialLayer, index: number): BreathDirection {
  if (layer === 'far') return pick(['drift', 'fade-only', 'drift', 'recede'])
  if (layer === 'near') return index % 2 === 0 ? 'approach' : 'drift'
  return index % 2 === 0 ? 'approach' : 'recede'
}

function cycleForLayer(): number {
  return randomInRange(...BRAND_BREATH_CYCLE_MS)
}

export function generateBrandBreathLayout(reducedMotion: boolean): BrandBreathLayout {
  const anchor = CANVAS_GLOBAL_SLOGANS.find((s) => s.locale === 'en')
  const rest = shuffled(CANVAS_GLOBAL_SLOGANS.filter((s) => s.locale !== 'en'))
  const zonePool = shuffled(BRAND_BREATH_SAFE_ZONES)

  const items: BrandBreathItemLayout[] = []

  if (anchor) {
    items.push(buildAnchorItem(anchor, reducedMotion))
  }

  rest.forEach((slogan, index) => {
    const zone = zonePool[index % zonePool.length]
    const spatialLayer = assignSpatialLayer(index, rest.length)
    items.push(buildItem(slogan, zone, spatialLayer, index, reducedMotion))
  })

  const keywords = reducedMotion ? [] : buildKeywords()

  return { items, keywords }
}

function buildAnchorItem(slogan: BrandBreathSlogan, reducedMotion: boolean): BrandBreathItemLayout {
  const { x, y } = jitterTranslate(reducedMotion, 'near')
  const cycleMs = reducedMotion ? 0 : randomInRange(...BRAND_BREATH_CYCLE_MS)
  return {
    locale: slogan.locale,
    text: slogan.text,
    direction: slogan.direction,
    tier: slogan.tier,
    spatialLayer: 'near',
    isAnchor: true,
    leftPercent: 50 + randomInRange(-2, 2),
    topPercent: 46 + randomInRange(-2, 2),
    fontSizePx: randomInRange(...TIER_FONT_PX[1]),
    fontFamily: slogan.fontFamily,
    targetOpacity: randomInRange(...BRAND_BREATH_ANCHOR_OPACITY),
    translateXPx: x * 0.2,
    translateYPx: y * 0.2,
    breathDirection: 'anchor-stable',
    breathCycleMs: cycleMs,
    phaseDelayMs: reducedMotion ? 0 : randomPhaseDelayMs(cycleMs),
  }
}

function buildItem(
  slogan: BrandBreathSlogan,
  zone: SafeZone,
  spatialLayer: SpatialLayer,
  index: number,
  reducedMotion: boolean,
): BrandBreathItemLayout {
  const { x, y } = jitterTranslate(reducedMotion, spatialLayer)
  const cycleMs = reducedMotion ? 0 : cycleForLayer()
  const isRtl = slogan.direction === 'rtl'
  const xRange: [number, number] = isRtl
    ? [Math.max(8, zone.xPercentRange[0]), Math.min(92, zone.xPercentRange[1])]
    : zone.xPercentRange
  return {
    locale: slogan.locale,
    text: slogan.text,
    direction: slogan.direction,
    tier: slogan.tier,
    spatialLayer,
    isAnchor: false,
    leftPercent: randomInRange(...xRange),
    topPercent: randomInRange(...zone.yPercentRange),
    fontSizePx: randomInRange(...TIER_FONT_PX[slogan.tier]),
    fontFamily: slogan.fontFamily,
    targetOpacity: randomInRange(...LAYER_OPACITY[spatialLayer]),
    translateXPx: x,
    translateYPx: y,
    breathDirection: reducedMotion ? 'fade-only' : pickMotionForLayer(spatialLayer, index),
    breathCycleMs: cycleMs,
    phaseDelayMs: reducedMotion ? 0 : randomPhaseDelayMs(cycleMs),
  }
}

function buildKeywords(): BrandBreathKeywordLayout[] {
  const count = Math.random() < 0.55 ? 2 : 3
  const pool = shuffled(CANVAS_GLOBAL_BRAND_BREATH_KEYWORDS).slice(0, count)
  return pool.map((text, index) => {
    const cycleMs = randomInRange(...BRAND_BREATH_KEYWORD_CYCLE_MS)
    return {
      id: `kw-${index}-${text}`,
      text,
      leftPercent: randomInRange(12, 88),
      topPercent: randomInRange(12, 88),
      fontSizePx: randomInRange(...BRAND_BREATH_KEYWORD_FONT_PX),
      targetOpacity: randomInRange(...BRAND_BREATH_KEYWORD_OPACITY),
      breathCycleMs: cycleMs,
      phaseDelayMs: randomPhaseDelayMs(cycleMs),
    }
  })
}
